/**
 * Firestore + Storage data access (doc 147 §7.4, §9 Phase 1).
 *
 * Atlas listens to the tiny `/manifests/current` pointer doc; on change it
 * resolves the Cloud Storage object and fetches the JSON. Per-user state
 * (recents · pinned · density) is read/written under `/users/{uid}`.
 *
 * Pure helpers (`gsPathFromUrl`, `normalizeManifestPointer`) are unit-tested;
 * the SDK calls around them are thin and degrade gracefully.
 */
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import type { AtlasManifest } from '@atlas-manifest/types';
import { db, storage } from './client.js';
import { normalizeUserState, type UserStateDoc } from '../state/userState.js';
import {
  bookmarkDocSegments,
  normalizeBookmark,
  type BookmarkSlot,
  type CameraBookmark,
} from '../graph3d/cameraBookmarks.js';

export interface ManifestPointerStats {
  entityCount: number;
  domainCount: number;
  referenceCount: number;
  surfaceCount: number;
}

export interface ManifestPointer {
  url: string;
  sha256: string;
  generatedAt: string;
  generatedFromCommit: string;
  /** Cloud Storage URL of the pre-computed constellation layout, when present. */
  layoutUrl?: string;
  /** Aggregate counts written by publishManifest — available before full manifest fetch. */
  stats?: ManifestPointerStats;
}

/** Extract the object path from a `gs://bucket/path/to/object` URL. */
export function gsPathFromUrl(gsUrl: string): string {
  const m = gsUrl.match(/^gs:\/\/[^/]+\/(.+)$/);
  if (!m || !m[1]) {
    throw new Error(`Not a gs:// object URL: ${gsUrl}`);
  }
  return m[1];
}

/** Coerce an untrusted pointer doc into a {@link ManifestPointer}, or null. */
export function normalizeManifestPointer(raw: unknown): ManifestPointer | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.url !== 'string' ||
    typeof r.sha256 !== 'string' ||
    typeof r.generatedAt !== 'string' ||
    typeof r.generatedFromCommit !== 'string'
  ) {
    return null;
  }
  const pointer: ManifestPointer = {
    url: r.url,
    sha256: r.sha256,
    generatedAt: r.generatedAt,
    generatedFromCommit: r.generatedFromCommit,
    ...(typeof r.layoutUrl === 'string' ? { layoutUrl: r.layoutUrl } : {}),
  };
  // Pass through stats when present and well-formed so consumers can show
  // aggregate counts before downloading the full manifest blob.
  if (typeof r.stats === 'object' && r.stats !== null) {
    const s = r.stats as Record<string, unknown>;
    if (
      typeof s.entityCount === 'number' &&
      typeof s.domainCount === 'number' &&
      typeof s.referenceCount === 'number' &&
      typeof s.surfaceCount === 'number'
    ) {
      pointer.stats = {
        entityCount: s.entityCount,
        domainCount: s.domainCount,
        referenceCount: s.referenceCount,
        surfaceCount: s.surfaceCount,
      };
    }
  }
  return pointer;
}

/* c8 ignore start — thin Firestore/Storage SDK wrappers; emulator tested. */

/**
 * Subscribe to the manifest pointer doc. `onPointer` fires with the normalized
 * pointer (or `null` when the doc is missing/malformed). Returns unsubscribe.
 */
export function watchManifestPointer(
  onPointer: (pointer: ManifestPointer | null) => void,
  onError: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'manifests/current'),
    (snap) => onPointer(normalizeManifestPointer(snap.data())),
    (err) => onError(err),
  );
}

/** Resolve a pointer's Cloud Storage object and fetch the manifest JSON. */
export async function fetchManifest(pointer: ManifestPointer): Promise<AtlasManifest> {
  const path = gsPathFromUrl(pointer.url);
  const downloadUrl = await getDownloadURL(ref(storage, path));
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error(`Manifest fetch failed: ${res.status}`);
  }
  return (await res.json()) as AtlasManifest;
}

/** Subscribe to a user's persisted state under `/users/{uid}/state/current`. */
export function watchUserState(
  uid: string,
  onState: (state: UserStateDoc) => void,
): () => void {
  return onSnapshot(
    doc(db, `users/${uid}/state/current`),
    (snap) => onState(normalizeUserState(snap.data())),
    (err) => {
      // A listener failure (e.g. permission revoked) must surface, not vanish —
      // then degrade to defaults so navigation keeps working.
      console.error('[atlas] watchUserState subscription error:', err);
      onState(normalizeUserState(null));
    },
  );
}

/** Persist a patch to a user's state doc. Best-effort — failures are swallowed. */
export async function saveUserState(uid: string, patch: Partial<UserStateDoc>): Promise<void> {
  try {
    await setDoc(doc(db, `users/${uid}/state/current`), patch, { merge: true });
  } catch {
    // A persistence failure must never break navigation (graceful degradation).
  }
}

/**
 * Save a 3D camera bookmark for a user slot. Multi-segment `doc()` makes
 * path traversal impossible even if `uid` is attacker-controlled — Firestore
 * SDK rejects `/` inside a single segment. Invalid bookmarks are dropped
 * before they hit the network. Failures degrade silently.
 */
export async function saveCameraBookmark(
  uid: string,
  slot: BookmarkSlot,
  bookmark: CameraBookmark,
): Promise<void> {
  const safe = normalizeBookmark(bookmark);
  if (!safe) return;
  try {
    const [a, b, c, d] = bookmarkDocSegments(uid, slot);
    await setDoc(doc(db, a, b, c, d), safe);
  } catch {
    /* graceful degradation */
  }
}

/** Load a 3D camera bookmark; returns null when missing or malformed. */
export async function loadCameraBookmark(
  uid: string,
  slot: BookmarkSlot,
): Promise<CameraBookmark | null> {
  try {
    const [a, b, c, d] = bookmarkDocSegments(uid, slot);
    const snap = await getDoc(doc(db, a, b, c, d));
    return normalizeBookmark(snap.data());
  } catch {
    return null;
  }
}

/* c8 ignore stop */
